// sentItemsView — the PURE logic behind the Sent Items surface.
//
// No React, no network, no `@/lib/api` in the import graph — so this module is
// unit-testable with plain `npx tsx --test`, without the module-mock flag the
// component tests need (T21). Everything that has to be RIGHT rather than
// merely rendered lives here: the block order the panel reads top-to-bottom,
// which filters live in the URL, how long something has been waiting.
//
// Types come in as `import type` only, deliberately: sentItemsClient.ts
// imports `@/lib/api` for its fetchers, and a runtime import of one of its
// constants would drag that whole chain in here and make this file untestable
// without the flag.

import { docStateTone } from '@/lib/viewDocs';
import type { DocTone } from '@/lib/viewDocs';
import type {
  LifecycleStep,
  SentItemKind,
  SentItemRow,
  SentItemTypeGroup,
  SentItemsListParams,
  SentItemsView,
  ViewDocState,
  WaitingParty,
  OverviewPhase,
} from '@/lib/sentItemsClient';

// ---------------------------------------------------------------------------
// The three views (§6.1)
// ---------------------------------------------------------------------------

/** Every view, in segmented-control order. Restated (not imported) so this
 *  module stays free of the client's runtime import chain. */
export const VIEWS: readonly SentItemsView[] = ['all', 'waiting', 'recent'];

/** The switcher's labels — Miles's own framing of what each view answers. */
export const VIEW_LABEL: Record<SentItemsView, string> = {
  all: 'Everything',
  waiting: "Who's waiting",
  recent: 'Latest activity',
};

/** The second line under each label, so a view says what it orders by. */
export const VIEW_HINT: Record<SentItemsView, string> = {
  all: 'newest first',
  waiting: 'in motion · longest first',
  recent: 'in motion · newest first',
};

export function isSentItemsView(value: string | null | undefined): value is SentItemsView {
  return value === 'all' || value === 'waiting' || value === 'recent';
}

/** Only `all` is cursor-paginated; the in-motion views return the whole set. */
export function viewIsPaginated(view: SentItemsView): boolean {
  return view === 'all';
}

const VIEW_STORAGE_KEY = 'whp.sentItems.view';

/**
 * The remembered view.
 *
 * Storage is wrapped because it THROWS, not merely returns null, in a Safari
 * private window and under some enterprise policies — an operator with cookies
 * locked down must still get a page, so a storage failure degrades to the
 * default rather than blanking the app.
 */
export function readStoredView(storage?: Pick<Storage, 'getItem'>): SentItemsView | null {
  try {
    const store = storage ?? globalThis.localStorage;
    const raw = store?.getItem(VIEW_STORAGE_KEY);
    return isSentItemsView(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeStoredView(
  view: SentItemsView,
  storage?: Pick<Storage, 'setItem'>,
): void {
  try {
    const store = storage ?? globalThis.localStorage;
    store?.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    /* a browser that refuses to remember is not an error worth showing */
  }
}

// ---------------------------------------------------------------------------
// URL state (§6.2 — "all in the URL as search params so a view is shareable")
// ---------------------------------------------------------------------------

const TYPE_GROUPS: readonly SentItemTypeGroup[] = [
  'lease',
  'intake',
  'verification',
  'condition_report',
  'addendum',
  'payment',
  'moveout',
  'owner',
  'accommodation',
  'other',
];

const STATES: readonly ViewDocState[] = [
  'draft',
  'sent',
  'in_progress',
  'completed',
  'declined',
  'cancelled',
  'voided',
  'superseded',
];

function isTypeGroup(v: string): v is SentItemTypeGroup {
  return (TYPE_GROUPS as readonly string[]).includes(v);
}

function isState(v: string): v is ViewDocState {
  return (STATES as readonly string[]).includes(v);
}

/** The lifecycle steps, in lifecycle order. Restated for the same reason
 *  `STATES` is — this module must not pull the client's runtime chain in. */
const STEPS: readonly LifecycleStep[] = [
  'drafting',
  'generated',
  'sending',
  'out',
  'countersign',
  'executed',
  'syncing',
  'synced',
  'closed',
];

function isStep(v: string): v is LifecycleStep {
  return (STEPS as readonly string[]).includes(v);
}

const PHASES: readonly OverviewPhase[] = [
  'preparing',
  'ready',
  'out',
  'countersign',
  'done',
  'blocked',
  'closed',
];

function isPhase(v: string): v is OverviewPhase {
  return (PHASES as readonly string[]).includes(v);
}

function isParty(v: string): v is WaitingParty {
  return v === 'whp' || v === 'recipient' || v === 'system';
}

/**
 * The URL, read as list params.
 *
 * Unknown values are DROPPED rather than passed through: a hand-edited
 * `?state=nonsense` should show the unfiltered list, not ask the server what
 * it thinks a nonsense state is. `from`/`to` only mean anything on `all`
 * (§6.2), so they are ignored on the two in-motion views instead of being
 * quietly sent and quietly ignored somewhere else.
 */
export function paramsFromSearch(search: URLSearchParams): SentItemsListParams {
  const view = isSentItemsView(search.get('view')) ? (search.get('view') as SentItemsView) : 'all';
  const types = (search.get('types') ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(isTypeGroup);
  const state = search.get('state');
  const step = search.get('step');
  const party = search.get('party');
  const q = search.get('q') ?? '';
  const from = search.get('from');
  const to = search.get('to');

  const params: SentItemsListParams = { view };
  if (types.length > 0) params.types = types;
  // `renewals=1` is the Renewal chip; `renewals=0` is the Leases lane's complement.
  if (search.get('renewals') === '1') params.renewalsOnly = true;
  if (search.get('renewals') === '0') params.excludeRenewals = true;
  if (state && isState(state)) params.state = state;
  if (step && isStep(step)) params.step = step;
  const phase = search.get('phase');
  if (phase && isPhase(phase)) params.phase = phase;
  if (party && isParty(party)) params.party = party;
  if (search.get('blocked') === '1') params.blocked = true;
  if (q.trim()) params.q = q;
  if (view === 'all' && from) params.from = from;
  if (view === 'all' && to) params.to = to;
  return params;
}

/**
 * A partial update to the filters.
 *
 * `Partial<SentItemsListParams>` is NOT this type: under
 * `exactOptionalPropertyTypes` an optional property still refuses an explicit
 * `undefined`, and "clear this filter" is exactly `{ state: undefined }`. So
 * the patch says so out loud.
 */
export type SentItemsFilterPatch = {
  [K in keyof SentItemsListParams]?: SentItemsListParams[K] | undefined;
};

/** The inverse: params written back onto a URLSearchParams, in place. */
export function applyParamsToSearch(
  search: URLSearchParams,
  params: SentItemsFilterPatch,
): URLSearchParams {
  const set = (key: string, value: string | null | undefined) => {
    if (value) search.set(key, value);
    else search.delete(key);
  };
  set('view', params.view && params.view !== 'all' ? params.view : null);
  set('types', params.types && params.types.length > 0 ? params.types.join(',') : null);
  set('renewals', params.renewalsOnly ? '1' : params.excludeRenewals ? '0' : null);
  set('state', params.state ?? null);
  set('step', params.step ?? null);
  set('phase', params.phase ?? null);
  set('party', params.party ?? null);
  set('blocked', params.blocked ? '1' : null);
  set('q', params.q?.trim() ? params.q : null);
  set('from', params.view === 'all' ? (params.from ?? null) : null);
  set('to', params.view === 'all' ? (params.to ?? null) : null);
  return search;
}

/** True when anything narrows the list — drives the "Clear filters" button. */
export function hasActiveFilters(params: SentItemsFilterPatch): boolean {
  return Boolean(
    (params.types && params.types.length > 0) ||
      params.renewalsOnly ||
      params.excludeRenewals ||
      params.state ||
      params.step ||
      params.phase ||
      params.party ||
      params.blocked ||
      params.q?.trim() ||
      params.from ||
      params.to,
  );
}

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "4 min ago", "3 days ago", "12 Aug" — the row's when-column.
 *
 * A future timestamp reads as "just now" rather than "in 3 minutes": clock
 * skew between the API host and a laptop is real and small, and an operator
 * seeing the future in an activity column learns to distrust the column.
 */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const delta = now.getTime() - then.getTime();
  if (delta < 45_000) return 'just now';
  if (delta < 90 * MINUTE) {
    const mins = Math.round(delta / MINUTE);
    return `${mins} min ago`;
  }
  if (delta < 36 * HOUR) {
    const hours = Math.round(delta / HOUR);
    return `${hours} hr ago`;
  }
  if (delta < 30 * DAY) {
    const days = Math.round(delta / DAY);
    return `${days} days ago`;
  }
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (then.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return then.toLocaleDateString(undefined, opts);
}

/** The exact instant, for the `title=` on a relative time. */
export function absoluteTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Row display
// ---------------------------------------------------------------------------

/** "108 SE 6th St · #8", skipping whichever half we don't have. */
export function whereLabel(row: Pick<SentItemRow, 'propertyLabel' | 'unitLabel'>): string {
  return [row.propertyLabel, row.unitLabel].filter(Boolean).join(' · ');
}

/** The chip tone for the row's state — the same table /docs uses. */
export function rowStateTone(row: Pick<SentItemRow, 'state' | 'lifecycle'>): DocTone {
  if (row.lifecycle.blocked) return 'blocked';
  return docStateTone(row.state);
}

/** Blocked is either a stuck lifecycle or a blocking signal (§6.2). */
export function rowIsBlocked(row: Pick<SentItemRow, 'lifecycle' | 'signals'>): boolean {
  return row.lifecycle.blocked === true || row.signals.block > 0;
}

/** The one line the "waiting on" column shows; finished rows say nothing. */
export function waitingLabel(row: Pick<SentItemRow, 'waitingOn'>): string {
  return row.waitingOn?.label?.trim() || '—';
}

/**
 * The design-system glyph for a row kind (T29 — the curated icon set, never
 * emoji). Kinds share glyphs where they share a meaning: every addendum is a
 * document, every owner-side form is about a person.
 */
export type SentItemGlyph =
  | 'file'
  | 'users'
  | 'check'
  | 'home'
  | 'key'
  | 'banknote'
  | 'dollar'
  | 'attach'
  | 'flag'
  | 'user'
  | 'calendar';

const GLYPH_BY_KIND: Record<SentItemKind, SentItemGlyph> = {
  lease: 'key',
  adoption_addendum: 'file',
  leaseholder_change: 'file',
  leaseholder_removal: 'file',
  payment_plan: 'banknote',
  animal_addendum: 'file',
  animal_addendum_paid: 'file',
  condition_report: 'check',
  moveout_funds_direction: 'dollar',
  manual_document: 'attach',
  property_management_agreement: 'home',
  owner_contact_addendum: 'user',
  w9: 'dollar',
  // The City of Walla Walla utilities agreement — it sends the property's bills
  // to us, so it carries the property's glyph.
  city_utility_agreement: 'home',
  intake: 'users',
  rental_verification: 'check',
  owner_intake: 'user',
  ccr_upload: 'attach',
  rra_intake: 'flag',
  rra_verification: 'flag',
};

export function glyphForKind(kind: string): SentItemGlyph {
  return GLYPH_BY_KIND[kind as SentItemKind] ?? 'file';
}

// ---------------------------------------------------------------------------
// The panel's block order (§6.4, Q5)
// ---------------------------------------------------------------------------

/**
 * The five ordered blocks. `standing` is "where it stands + what you can do
 * about it"; `terms` is the registry panel; `document` is the PDF/replica.
 */
export type PanelBlock = 'standing' | 'recipients' | 'activity' | 'terms' | 'document';

const IN_MOTION_ORDER: readonly PanelBlock[] = [
  'standing',
  'recipients',
  'activity',
  'terms',
  'document',
];

const FINISHED_ORDER: readonly PanelBlock[] = ['terms', 'document', 'recipients', 'activity'];

/**
 * Q5, stated once: an item still moving is asked "where does this stand and
 * what do I do"; a finished one is asked "what does it say and can I have a
 * copy". A finished row therefore has NO standing block — its header already
 * carries the state and the date, and a "where it stands" heading over "Fully
 * executed" is a question nobody is asking any more.
 */
export function panelBlockOrder(inMotion: boolean): readonly PanelBlock[] {
  return inMotion ? IN_MOTION_ORDER : FINISHED_ORDER;
}

/** Finished panels open with Activity collapsed (§6.4). */
export function activityStartsCollapsed(inMotion: boolean): boolean {
  return !inMotion;
}

/** Steps at which an item has stopped moving — the `inMotion` complement. */
const FINISHED_STEPS: readonly LifecycleStep[] = ['executed', 'synced', 'closed'];

/**
 * Trust the server's `inMotion` (§5.6 is ONE pure function, server-side), and
 * fall back to the step only when a fixture or an older payload omits it.
 */
export function rowInMotion(row: Pick<SentItemRow, 'inMotion' | 'lifecycle'>): boolean {
  if (typeof row.inMotion === 'boolean') return row.inMotion;
  return !FINISHED_STEPS.includes(row.lifecycle.step);
}

/**
 * Which blocks actually have something in them (T14: a draft has no
 * recipients and no activity, and a heading over nothing reads as data we
 * failed to load).
 */
export function nonEmptyBlocks(
  order: readonly PanelBlock[],
  has: { recipients: boolean; activity: boolean; terms: boolean; document: boolean },
): PanelBlock[] {
  return order.filter((block) => {
    switch (block) {
      case 'standing':
        return true;
      case 'recipients':
        return has.recipients;
      case 'activity':
        return has.activity;
      case 'terms':
        return has.terms;
      case 'document':
        return has.document;
    }
  });
}

/** Panel headings, so the two callers (drawer, household inline) agree. */
export const BLOCK_HEADING: Record<PanelBlock, string> = {
  standing: 'Where it stands',
  recipients: 'Sent to',
  activity: 'Activity',
  terms: 'Key terms',
  document: 'Document',
};

// ---------------------------------------------------------------------------
// Actions (§6.6)
// ---------------------------------------------------------------------------

/** At most three primary actions; everything else goes under "More". */
export const MAX_PRIMARY_ACTIONS = 3;

/**
 * Actions that restart a request staff cancelled ("Ask again"). Their ids start `send-again`.
 * A cancelled item is FINISHED, so the standing block that normally carries actions is not
 * drawn for it — these are drawn on their own, so a cancel is never a dead end.
 */
export function restartActions<T extends { id: string; method: string }>(
  actions: readonly T[],
): T[] {
  return actions.filter((a) => a.method !== 'navigate' && a.id.startsWith('send-again'));
}

export function splitActions<T>(actions: readonly T[]): { primary: T[]; more: T[] } {
  return {
    primary: actions.slice(0, MAX_PRIMARY_ACTIONS),
    more: actions.slice(MAX_PRIMARY_ACTIONS),
  };
}

/** Newest first, capped — the API sends activity newest LAST (T18). */
export function newestFirstCapped<T extends { at: string }>(
  activity: readonly T[],
  cap: number,
): { entries: T[]; hidden: number } {
  const ordered = activity
    .map((event, i) => ({ event, i }))
    .sort((a, b) => b.event.at.localeCompare(a.event.at) || b.i - a.i)
    .map((x) => x.event);
  return { entries: ordered.slice(0, cap), hidden: Math.max(0, ordered.length - cap) };
}
